import { BaseQueryFn, FetchArgs, FetchBaseQueryError, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setLastActiveSession, setLogoutPending, setTokens } from "features/authSlice";
import { TokensRequest, authenticationApi } from "pages/authentication/authenticationService";
import { RootState } from "store";
import * as Constant from "utilities/Constant";
import { minutesToSecondsConvertion, subtractDates } from "utilities/date";

export const baseQuery = fetchBaseQuery({ baseUrl: Constant.BASE_BFF_WEB_URL });

const baseQueryWithHeader = fetchBaseQuery({
  baseUrl: Constant.BASE_BFF_WEB_URL,
  prepareHeaders: (headers: Headers, { getState }) => {
    const accessToken = (getState() as RootState).auth.accessToken;
    if (accessToken) {
      headers.set("authorization", `Bearer ${accessToken}`);
    }
    return headers;
  },
});

let refreshPromise: Promise<any> | null = null;

export const baseQueryWithAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const state = api.getState() as RootState;

  // If logout is already pending, short-circuit — don't start new requests
  if (state.auth.logoutPending) {
    return { error: { status: 401, data: "Session ended" } as FetchBaseQueryError };
  }

  const { refreshToken, lastActiveTime, tokenExpiration } = state.auth.tokenValidationParams;
  const accessToken = state.auth.accessToken;
  const currentDate = new Date();
  const isUserActiveSessionLimitExceeds =
    subtractDates(currentDate, lastActiveTime) > minutesToSecondsConvertion(Constant.ACCESS_TOKEN_EXPIRATION_MINUTES);
  const isTokenExpired = subtractDates(tokenExpiration, currentDate) < minutesToSecondsConvertion(Constant.SKEW_TIME_IN_MINUTES);

  if (isUserActiveSessionLimitExceeds) {
    // Mark logout pending — the listener will handle cleanup
    api.dispatch(setLogoutPending());
    return { error: { status: 401, data: "Session inactive" } as FetchBaseQueryError };
  }

  if (isTokenExpired) {
    const tokenRequestData: TokensRequest = {
      accessToken: accessToken,
      refreshToken: refreshToken,
    };

    if (!refreshPromise && tokenRequestData.accessToken) {
      refreshPromise = api
        .dispatch(authenticationApi.endpoints.getNewTokens.initiate(tokenRequestData))
        .unwrap()
        .then((data) => {
          api.dispatch(setTokens(data));
        })
        .catch(() => {
          // Mark pending — don't nuke state immediately
          api.dispatch(setLogoutPending());
        })
        .finally(() => { refreshPromise = null; });
    }
    await refreshPromise;

    // After refresh attempt, check if logout was triggered
    if ((api.getState() as RootState).auth.logoutPending) {
      return { error: { status: 401, data: "Token refresh failed" } as FetchBaseQueryError };
    }
  }

  const result = await baseQueryWithHeader(args, api, extraOptions);

  if (result.error?.status === 401 || result.error?.status === 403) {
    // Mark pending instead of immediate logout
    api.dispatch(setLogoutPending());
  }

  api.dispatch(setLastActiveSession(new Date()));
  return result;
};
